import { handleError } from '../context/errors/errorUtils';
import { sendLessonData } from '../api/fetchLessons';
import { sendExamData } from '../api/fetchExam';
import { sendGameData } from '../api/fetchGame';
import { sendPathGameData } from '../api/fetchPathGame';
import { sendDialogData } from '../api/fetchDialogs';

const DIALOG_STEP_TRANSITION_DELAY_MS = 1900;
const DIALOG_MAX_ATTEMPTS_BEFORE_FALLBACK = 3;
const DIALOG_FALLBACK_OPTION_NUMBER = 1;

const withTimeout = async (promise, timeoutMs, context = {}) => {
  try {
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Operation timeout')), timeoutMs);
    });
    return await Promise.race([promise, timeout]);
  } catch (err) {
    handleError({
      code: 'TIMEOUT_ERROR',
      error: err,
      context: { module: 'generalHandlers', ...context },
      showToUser: false,
    });
    throw err;
  }
};

export const sendData = async (data, audioBuffer, params, source) => {
  const payload = {
    ...data,
    ...params,
    audioBuffer,
    source,
  };

  const fetchFunction = {
    lesson: sendLessonData,
    exam: sendExamData,
    game: sendGameData,
    pathgame: sendPathGameData,
    dialog: sendDialogData,
  }[source];

  if (!fetchFunction) {
    throw new Error(`Unknown source: ${source}`);
  }

  const response = await fetchFunction(payload);

  return {
    server: true,
    error: response.error || null,
    success: response.success || false,

    lessonId: response.lessonId || payload.lessonId,
    examId: response.examId || payload.examId,
    dialogId: response.dialogId || payload.dialogId,

    blockId: response.blockId || payload.step,
    step: response.step || payload.step,
    transcript: response.transcript || payload.userText || '',
    score: response.score || 0,

    correctAnswer: response.correctAnswer || false,
    matchedOptionIndex:
      response.matchedOptionIndexZeroBased !== undefined
        ? response.matchedOptionIndexZeroBased
        : (response.matchedOptionIndex ?? payload.matchedOptionIndex ?? 0),

    result: response.result || null,
    language: response.language || payload.language || 'de',
    progress: response.progress || 0,
    totalBlocks: response.totalBlocks || 10,
    nextExamStep: response.nextExamStep || false,

    endDialog: response.endDialog || false,
    totalSteps: response.totalSteps || 0,
    dialogHistory: response.dialogHistory || [],
    optionNumber: response.optionNumber || 0,
    introText: response.introText || '',
    introTranslate: response.introTranslate || '',
    introAudio: response.introAudio || '',
    introImage: response.introImage || '',
    assistantText: response.assistantText || '',
    assistantTextTranslate: response.assistantTextTranslate || '',
    assistantAudioUrl: response.assistantAudioUrl || '',
    options: Array.isArray(response.options) ? response.options : [],
    learnDialogTitle: response.learnDialogTitle || '',
  };
};

export const handleStopRecording = async (audioBlob, data, dependencies) => {
  const { dispatch, step, state, contextId, source, userId, onComplete } = dependencies;

  try {
    if (data?.error) {
      handleError({
        code: 'RECOGNITION_ERROR',
        error: new Error(data.error),
        context: { module: 'generalHandlers', contextId, userId, stepId: step.id },
        showToUser: false,
      });
      dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
      dispatch({ type: 'INCREMENT_ATTEMPT' });
      onComplete();
      return;
    }

    const hasLocalMatch = data?.recognizedText && data?.matchedOptionIndex !== null && data?.matchedOptionIndex !== undefined;
    if (hasLocalMatch && source !== 'dialog') {
      data.correctAnswer = true;
      processSuccessfulResponse(data, { dispatch, step, state, source });
      onComplete();
      return;
    }

    const baseParams = {
      userId,
      step: step.id,
      completeBlock: false,
    };

    // In dialogs, local recognition can intentionally return no audio blob.
    // We still send the matched option to backend to advance the dialog branch.
    if (source === 'dialog' && hasLocalMatch && !audioBlob) {
      const localMatchedOptionNumber = Number(data.matchedOptionIndex) + 1;
      const localMatchResponse = await withTimeout(
        sendData(data, null, {
          ...baseParams,
          dialogId: step.dialogId || contextId,
          attemptNumber: state.attemptCount + 1,
          correctAnswer: true,
          matchedOptionIndex: localMatchedOptionNumber,
        }, source),
        30000,
        { contextId, userId, stepId: step.id, localMatchOnly: true }
      );

      if (localMatchResponse.success !== true) {
        handleBackendError(localMatchResponse, dispatch);
        onComplete();
        return;
      }

      processSuccessfulResponse(localMatchResponse, { dispatch, step, state, source });
      onComplete();
      return;
    }

    if (!audioBlob) {
      handleError({
        code: 'AUDIO_BLOB_MISSING',
        error: new Error('audioBlob is missing'),
        context: { module: 'generalHandlers', contextId, userId, stepId: step.id },
        showToUser: true,
      });
      dispatch({ type: 'INCREMENT_ATTEMPT' });
      onComplete();
      return;
    }

    dispatch({ type: 'SET_AUDIO_BLOB', payload: audioBlob });
    dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: '' });

    const audioBuffer = await audioBlob.arrayBuffer();

    let sendParams = {};

    if (source === 'lesson') {
      const firstOption = step.options?.[0] || {};
      sendParams = {
        ...baseParams,
        lessonId: step.lessonId,
        phrasesCount: step.phrasesCount,
        attemptNumber: state.attemptCount + 1,
        userText: firstOption.userText || '',
        matchedOptionIndex: firstOption.index ?? 0,
        correctAnswer: firstOption.correct ?? false,
        userBlockId: contextId,
      };
    } else if (source === 'exam') {
      sendParams = {
        ...baseParams,
        examId: step.examId,
      };
    } else if (source === 'game' || source === 'pathgame') {
      const firstOption = step.options?.[0] || {};
      const resolvedPathStepId = step.pathStepId ?? step.pathStep ?? step.stepId ?? step.id;
      sendParams = {
        ...baseParams,
        phrasesCount: step.phrasesCount,
        gameId: step.gameId,
        ...(source === 'pathgame' ? { stepId: resolvedPathStepId } : {}),
        userText: firstOption.userText || '',
      };
    } else if (source === 'dialog') {
      const rawMatchedOptionIndex = hasLocalMatch ? data.matchedOptionIndex : null;
      const dialogMatchedOptionIndex =
        rawMatchedOptionIndex !== null && rawMatchedOptionIndex !== undefined
          ? rawMatchedOptionIndex + 1
          : (state.optionNumber || 0);

      sendParams = {
        ...baseParams,
        dialogId: step.dialogId || contextId,
        attemptNumber: state.attemptCount + 1,
        correctAnswer: Boolean(hasLocalMatch),
        matchedOptionIndex: dialogMatchedOptionIndex,
      };
    } else {
      sendParams = { ...baseParams };
    }

    const response = await withTimeout(
      sendData(data, audioBuffer, sendParams, source),
      30000,
      { contextId, userId, stepId: step.id }
    );

    if (response.success !== true) {
      const shouldFallbackToFirstDialogOption =
        source === 'dialog' &&
        response.error === 'AUDIO_NOT_RECOGNIZED' &&
        state.attemptCount + 1 >= DIALOG_MAX_ATTEMPTS_BEFORE_FALLBACK;

      if (shouldFallbackToFirstDialogOption) {
        const fallbackResponse = await withTimeout(
          sendData(data, null, {
            ...baseParams,
            dialogId: step.dialogId || contextId,
            attemptNumber: state.attemptCount + 1,
            correctAnswer: true,
            matchedOptionIndex: DIALOG_FALLBACK_OPTION_NUMBER,
          }, source),
          30000,
          { contextId, userId, stepId: step.id, dialogFallback: true }
        );

        if (fallbackResponse.success === true) {
          processSuccessfulResponse(fallbackResponse, { dispatch, step, state, source });
        } else {
          handleBackendError(fallbackResponse, dispatch);
        }
        onComplete();
        return;
      }

      handleBackendError(response, dispatch);
      onComplete();
      return;
    }

    processSuccessfulResponse(response, { dispatch, step, state, source });
    onComplete();
  } catch (err) {
    handleError({
      code: 'AUDIO_PROCESSING_ERROR',
      error: err,
      context: { module: 'generalHandlers', contextId, userId, stepId: step.id },
      showToUser: true,
    });
    dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
    dispatch({ type: 'INCREMENT_ATTEMPT' });
    onComplete();
  }
};

export const handleBackendError = (response, dispatch) => {
  const context = { module: 'generalHandlers', response };
  const errorMessage = response.message || 'Unknown error.';

  if (response.error === 'AUDIO_NOT_RECOGNIZED') {
    dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: response.transcript || '' });
    dispatch({ type: 'SET_MATCH_FAILED', payload: true });

    dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
    dispatch({ type: 'INCREMENT_ATTEMPT' });
  } else if (response.error === 'AUDIO_TIME_ENDED') {
    dispatch({ type: 'SET_AUDIO_TIME_ENDED', payload: true });
  } else if (response.error === 'TEST_ERROR') {
    handleError({
      code: 'TEST_ERROR',
      error: 'Backend test error',
      context,
      showToUser: true,
    });
  } else {
    handleError({
      code: 'UNKNOWN_BACKEND_ERROR',
      error: new Error(errorMessage),
      context,
      showToUser: true,
    });
  }
};

export const processSuccessfulResponse = (response, { dispatch, step, state, source }) => {
  if (source === 'dialog') {
    if (response.endDialog) {
      dispatch({ type: 'SET_END_DIALOG', payload: true });
      dispatch({ type: 'SET_TOTAL_STEP', payload: response.totalSteps || step.totalSteps || 0 });
      dispatch({ type: 'SET_DIALOG_HISTORY', payload: response.dialogHistory || [] });
      dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
      dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: '' });
      return;
    }

    const matchedOptionIndex =
      response.matchedOptionIndex !== undefined && response.matchedOptionIndex !== null
        ? response.matchedOptionIndex
        : null;

    if (matchedOptionIndex !== null) {
      dispatch({ type: 'SET_MATCHED_OPTION_INDEX', payload: matchedOptionIndex });
    }

    const nextStep = response.step || response.dialogStepId;

    const introData = nextStep === 1
      ? {
          introText: response.introText || '',
          introTranslate: response.introTranslate || '',
          introAudio: response.introAudio || '',
          introImage: response.introImage || '',
        }
      : null;

    const dialogStep = {
      id: nextStep || 0,
      totalSteps: response.totalSteps || step.totalSteps || 0,
      topic: response.learnDialogTitle || '',
      assistantText: response.assistantText || '',
      assistantTextTranslate: response.assistantTextTranslate || '',
      assistantAudioUrl: response.assistantAudioUrl || '',
      options: Array.isArray(response.options)
        ? response.options.map((option, index) => ({ ...option, index }))
        : [],
      language: response.language || state.language || 'de',
      dialogId: response.dialogId || step.dialogId,
    };

    if (introData) {
      dispatch({ type: 'SET_INTRO', payload: introData });
    }

    const applyNextDialogStep = () => {
      // Clear checkmark before step switch so it never leaks to the next step.
      dispatch({ type: 'SET_MATCHED_OPTION_INDEX', payload: null });
      dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: response.success || false });
      dispatch({ type: 'ADD_STEP', payload: dialogStep });
      dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });
      dispatch({ type: 'SET_SUBMITTED_TEXT', payload: '' });
      dispatch({ type: 'SET_AUDIO_BLOB', payload: null });
      dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: '' });
      dispatch({ type: 'SET_MATCH_FAILED', payload: false });
      dispatch({ type: 'RESET_ATTEMPT' });

      if (response.optionNumber !== undefined) {
        dispatch({ type: 'SET_OPTION_NUMBER', payload: response.optionNumber });
      }
    };

    if (matchedOptionIndex !== null) {
      setTimeout(applyNextDialogStep, DIALOG_STEP_TRANSITION_DELAY_MS);
    } else {
      applyNextDialogStep();
    }

    return;
  }

  const recognizedText = response.transcript || response.recognizedText || '';
  dispatch({ type: 'SET_RECOGNIZED_TEXT', payload: recognizedText });
  dispatch({ type: 'SET_AUDIO_BLOB', payload: null });

  if (response.result && source === 'exam') {
    dispatch({ type: 'SET_RESULT', payload: response.result });
  }

  if (response.nextExamStep && source === 'exam') {
    dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: true });
  }

  if (response.matchedOptionIndex !== null && source !== 'game') {
    dispatch({ type: 'SET_MATCHED_OPTION_INDEX', payload: response.matchedOptionIndex });
  }

  if (response.correctAnswer !== undefined) {
    dispatch({ type: 'SET_CORRECT_ANSWER', payload: response.correctAnswer });
  }

  if (response.matchedOptionIndex == null && response.correctAnswer == undefined && !response.nextExamStep) {
    dispatch({ type: 'SET_MATCH_FAILED', payload: true });
  }

  if (response.server === true && (source === 'lesson' || source === 'game' || source === 'pathgame')) {
    dispatch({ type: 'SET_SERVER_RESPONSE', payload: true });
  }
};
